"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "../../bausteine/alert";
import { DropdownMenue } from "../../bausteine/dropdown-menue";
import { Knopf } from "../../bausteine/knopf";
import { LeerZustand } from "../../bausteine/leerzustand";
import { Reiter, type ReiterEintrag } from "../../bausteine/reiter";
import {
  GLOCKE_FEHLSCHLAG_LISTE,
  GLOCKE_FEHLSCHLAG_MARKIEREN,
  glockeLabel,
  satzFuerAntwort,
  sichererZielpfad,
  zeitText,
  zeitVoll,
  type GlockeAktionen,
  type GlockeEintrag,
  type GlockeMenueProps,
} from "./glocke-logik";
import { AlleGelesenSymbol, ArtSymbol, GlockeSymbol, GlockeZaehler, ZahnradSymbol } from "./glocke-symbole";

/**
 * Benachrichtigungsmenü an der Glocke der Kopfzeile (Musterkatalog M-068, Design-Guide `glocke.md`; Preline-Block
 * `notifications-menu`). Der Baustein prüft nichts: Sitzung, Eigentum und Ablehnung liegen in den hereingereichten
 * Server-Actions (nur eigene Benachrichtigungen, SG-1/SG-2). Zähler und Gelesen-Stand gehören nur der eigenen Person (r177).
 *
 * Aufklappmenü wie das Dev-Menü (`DropdownMenue`, React-Zustand, Esc schließt und gibt den Fokus zurück); mobil volle Breite
 * mit 8 px Rand unter der Kopfzeile. Die Liste wird erst beim Öffnen geladen (kein Abruf je Seite). Archiv gibt es im
 * Datenmodell nicht: darum nur die Reiter „Alle“ und „Ungelesen“ (Abweichung AW-085 (1)).
 */

// Fläche wie der Katalogblock: mobil volle Breite (8 px Rand), ab sm w-96 mit Rahmen und Rundung (Abweichung AW-085 (2): Rand und Rundung auch mobil, wie das Dev-Menü).
// Zähler: `GlockeZaehler` (Badge „fehler“) statt der Roh-Palette des Blocks, Abweichung AW-085 (3); der Ping-Ring des Blocks entfällt (AW-085 (5)).
const MENUE_FLAECHE =
  "fixed top-0 start-0 inset-e-2 sm:inset-e-auto sm:w-96 z-70 bg-dropdown border border-dropdown-line rounded-xl shadow-md sm:shadow-xl overflow-hidden";
const AUSLOESER_HELL =
  "relative size-9.5 inline-flex justify-center items-center gap-x-2 border border-transparent text-muted-foreground-1 hover:bg-muted-hover focus:bg-muted-focus rounded-full disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden";
const AUSLOESER_DUNKEL =
  "relative inline-flex shrink-0 justify-center items-center gap-x-2 size-9.5 rounded-lg text-foreground-inverse hover:bg-plain/10 disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-plain/10";
const ZAHNRAD =
  "size-7 inline-flex justify-center items-center gap-x-2 rounded-full border border-transparent text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus";
const ZEILE =
  "relative w-full flex gap-x-5 text-start p-5 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus";
const ZEILE_UNGELESEN =
  "relative w-full flex gap-x-5 text-start p-5 bg-surface hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus";
const FUSS_EINTRAG =
  "p-4 flex grow basis-1/2 justify-center items-center gap-x-2 text-center text-sm text-muted-foreground-1 font-medium hover:text-primary-hover focus:outline-hidden focus:text-primary-hover disabled:opacity-50 disabled:pointer-events-none";

type Tab = "alle" | "ungelesen";
type Laden =
  | { zustand: "laedt" }
  | { zustand: "fehler"; satz: string }
  | { zustand: "fertig"; eintraege: GlockeEintrag[] };

export function GlockeMenue({ ungelesen: ungelesenProp, aktionen: aktionenProp, einstellungenHref, alleHref, ton = "hell", markierenErlaubt = true }: GlockeMenueProps) {
  // Die Actions kommen bei jedem Server-Render als neues Objekt; die Abfragen hängen nicht an ihrer Identität.
  const [aktionen] = useState(aktionenProp);
  const [ungelesen, setzeUngelesen] = useState(ungelesenProp);
  // Der Server ist die Quelle (nach `router.refresh()` kommen neue Props).
  useEffect(() => setzeUngelesen(ungelesenProp), [ungelesenProp]);

  return (
    <span className="inline-flex">
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-dropdown-menus/notifications-menu (Aufklappmenü mit Reitern, Liste, Fuß; mobil volle Breite) */}
      <DropdownMenue
        rechts
        flaecheKlasse={MENUE_FLAECHE}
        // Reiter, Zahnrad und Listeneinträge: Tab wandert durch die Fläche (schließt nicht), der Fokus startet am gewählten Reiter.
        tabSchliesst={false}
        fokusAuswahl='[role="tab"][aria-selected="true"]'
        ausloeserKlasse={ton === "dunkel" ? AUSLOESER_DUNKEL : AUSLOESER_HELL}
        ausloeserLabel={glockeLabel(ungelesen)}
        ausloeserTitel="Benachrichtigungen"
        ausloeser={
          <>
            <GlockeSymbol />
            <GlockeZaehler ungelesen={ungelesen} />
          </>
        }
      >
        {({ schliessen }) => (
          <Inhalt aktionen={aktionen} ungelesen={ungelesen} setzeUngelesen={setzeUngelesen} schliessen={schliessen} einstellungenHref={einstellungenHref} alleHref={alleHref} markierenErlaubt={markierenErlaubt} />
        )}
      </DropdownMenue>
    </span>
  );
}

function Inhalt({
  aktionen,
  ungelesen,
  setzeUngelesen,
  schliessen,
  einstellungenHref,
  alleHref,
  markierenErlaubt,
}: {
  aktionen: GlockeAktionen;
  ungelesen: number;
  setzeUngelesen: (n: number) => void;
  schliessen: () => void;
  einstellungenHref: string;
  alleHref: string;
  markierenErlaubt: boolean;
}) {
  const router = useRouter();
  const [tab, setzeTab] = useState<Tab>("alle");
  const [laden, setzeLaden] = useState<Laden>({ zustand: "laedt" });
  const [markierSatz, setzeMarkierSatz] = useState<string | null>(null);
  const [arbeitet, setzeArbeitet] = useState(false);
  const anfrage = useRef(0);

  const lade = useCallback(
    (welcher: Tab) => {
      const nr = ++anfrage.current;
      setzeLaden({ zustand: "laedt" });
      aktionen
        .liste({ nurUngelesen: welcher === "ungelesen" })
        .then((a) => {
          if (nr !== anfrage.current) return;
          if (a.ok) {
            setzeLaden({ zustand: "fertig", eintraege: a.eintraege });
            setzeUngelesen(a.ungelesen);
          } else setzeLaden({ zustand: "fehler", satz: satzFuerAntwort(a, GLOCKE_FEHLSCHLAG_LISTE) });
        })
        .catch(() => {
          if (nr === anfrage.current) setzeLaden({ zustand: "fehler", satz: GLOCKE_FEHLSCHLAG_LISTE });
        });
    },
    [aktionen, setzeUngelesen],
  );

  // Beim Öffnen (Einhängen) und bei jedem Reiterwechsel neu laden.
  useEffect(() => {
    lade(tab);
  }, [lade, tab]);

  /** Eintrag mit Ziel: Link öffnet das Ziel, die Markierung läuft daneben (ein Fehlschlag verhindert das Öffnen nie). */
  function markiereImHintergrund(e: GlockeEintrag) {
    schliessen();
    if (e.gelesen) return;
    aktionen
      .markiereGelesen(e.id)
      .then((a) => {
        if (a.ok) setzeUngelesen(a.ungelesen);
      })
      .catch(() => {
        /* Fehlschlag: der Eintrag bleibt ungelesen, das Ziel ist trotzdem offen; die Liste zeigt ihn beim nächsten Öffnen wieder */
      });
  }

  /** Eintrag ohne Ziel: nur als gelesen markieren, das Menü bleibt offen; ein Satz steht an der Liste, nie ein Toast. */
  async function markiereOhneZiel(e: GlockeEintrag) {
    if (e.gelesen || arbeitet) return;
    setzeMarkierSatz(null);
    setzeArbeitet(true);
    try {
      const a = await aktionen.markiereGelesen(e.id);
      if (a.ok) {
        setzeUngelesen(a.ungelesen);
        setzeLaden((alt) =>
          alt.zustand === "fertig"
            ? { zustand: "fertig", eintraege: tab === "ungelesen" ? alt.eintraege.filter((x) => x.id !== e.id) : alt.eintraege.map((x) => (x.id === e.id ? { ...x, gelesen: true } : x)) }
            : alt,
        );
      } else setzeMarkierSatz(satzFuerAntwort(a, GLOCKE_FEHLSCHLAG_MARKIEREN));
    } catch {
      setzeMarkierSatz(GLOCKE_FEHLSCHLAG_MARKIEREN);
    } finally {
      setzeArbeitet(false);
    }
  }

  async function markiereAlle() {
    if (arbeitet) return;
    setzeMarkierSatz(null);
    setzeArbeitet(true);
    try {
      const a = await aktionen.markiereAlleGelesen();
      if (a.ok) {
        setzeUngelesen(a.ungelesen);
        setzeLaden((alt) =>
          alt.zustand === "fertig"
            ? { zustand: "fertig", eintraege: tab === "ungelesen" ? [] : alt.eintraege.map((x) => ({ ...x, gelesen: true })) }
            : alt,
        );
        router.refresh(); // Zähler an Gruppe und Symbol neu vom Server
      } else setzeMarkierSatz(satzFuerAntwort(a, GLOCKE_FEHLSCHLAG_MARKIEREN));
    } catch {
      setzeMarkierSatz(GLOCKE_FEHLSCHLAG_MARKIEREN);
    } finally {
      setzeArbeitet(false);
    }
  }

  const liste = (welcher: Tab) =>
    tab !== welcher ? null : (
      <Liste
        laden={laden}
        ungelesenTab={welcher === "ungelesen"}
        markierenErlaubt={markierenErlaubt}
        beiWiederholen={() => lade(welcher)}
        beiMarkierenOhneZiel={(e) => void markiereOhneZiel(e)}
        beiZiel={markiereImHintergrund}
      />
    );

  const eintraege: ReiterEintrag[] = [
    { id: "alle", label: "Alle", inhalt: liste("alle") },
    { id: "ungelesen", label: "Ungelesen", inhalt: liste("ungelesen") },
  ];

  return (
    <div>
      <Reiter
        beschriftung="Benachrichtigungen"
        eintraege={eintraege}
        abstand="karte"
        panelAbstand="keiner"
        beimWechsel={(id) => setzeTab(id === "ungelesen" ? "ungelesen" : "alle")}
        leistenZusatz={
          <Link href={einstellungenHref} role="menuitem" className={ZAHNRAD} aria-label="Einstellungen für Benachrichtigungen" title="Einstellungen" onClick={schliessen}>
            <ZahnradSymbol />
          </Link>
        }
      />
      {markierSatz ? (
        <div className="p-3 border-t border-dropdown-divider">
          <Alert ton="warnung">{markierSatz}</Alert>
        </div>
      ) : null}
      <div className="flex border-t border-dropdown-divider divide-x divide-dropdown-divider">
        {markierenErlaubt ? (
          <button type="button" role="menuitem" className={FUSS_EINTRAG} disabled={ungelesen === 0 || arbeitet} onClick={() => void markiereAlle()}>
            <AlleGelesenSymbol />
            Alle als gelesen markieren
          </button>
        ) : null}
        <Link href={alleHref} role="menuitem" className={FUSS_EINTRAG} onClick={schliessen}>
          Alle anzeigen
        </Link>
      </div>
    </div>
  );
}

function Liste({
  laden,
  ungelesenTab,
  markierenErlaubt,
  beiWiederholen,
  beiMarkierenOhneZiel,
  beiZiel,
}: {
  laden: Laden;
  ungelesenTab: boolean;
  markierenErlaubt: boolean;
  beiWiederholen: () => void;
  beiMarkierenOhneZiel: (e: GlockeEintrag) => void;
  beiZiel: (e: GlockeEintrag) => void;
}) {
  if (laden.zustand === "laedt") {
    return (
      <p className="p-5 text-sm text-muted-foreground-1" role="status" aria-busy="true">
        Lade Benachrichtigungen …
      </p>
    );
  }
  if (laden.zustand === "fehler") {
    return (
      <div className="p-3 space-y-3">
        <Alert ton="warnung">{laden.satz}</Alert>
        <Knopf groesse="xs" variante="sekundaer" onClick={beiWiederholen}>
          Erneut versuchen
        </Knopf>
      </div>
    );
  }
  if (laden.eintraege.length === 0) {
    return ungelesenTab ? (
      <LeerZustand titel="Alles gelesen." text="Es gibt keine ungelesenen Benachrichtigungen." symbol={<GlockeSymbol />} />
    ) : (
      <LeerZustand titel="Noch keine Benachrichtigungen." text="Sobald es etwas für dich gibt, steht es hier." symbol={<GlockeSymbol />} />
    );
  }
  return (
    <div className="max-h-96 sm:max-h-120 overflow-y-auto">
      <ul className="divide-y divide-dropdown-divider" aria-label={ungelesenTab ? "Ungelesene Benachrichtigungen" : "Benachrichtigungen"}>
        {laden.eintraege.map((e) => (
          <li key={e.id}>
            <Zeile eintrag={e} markierenErlaubt={markierenErlaubt} beiMarkierenOhneZiel={beiMarkierenOhneZiel} beiZiel={beiZiel} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Zeile({
  eintrag,
  markierenErlaubt,
  beiMarkierenOhneZiel,
  beiZiel,
}: {
  eintrag: GlockeEintrag;
  markierenErlaubt: boolean;
  beiMarkierenOhneZiel: (e: GlockeEintrag) => void;
  beiZiel: (e: GlockeEintrag) => void;
}) {
  const ziel = sichererZielpfad(eintrag.ziel);
  const inhalt = (
    <>
      <span className="relative shrink-0">
        <span className="flex shrink-0 justify-center items-center size-9.5 bg-layer border border-layer-line text-layer-foreground rounded-full shadow-2xs">
          <ArtSymbol art={eintrag.art} />
        </span>
        {!eintrag.gelesen && <span className="absolute top-4 -inset-s-3 size-2 bg-primary rounded-full" role="img" aria-label="ungelesen" />}
      </span>
      <span className="grow min-w-0">
        <time className="block text-xs text-muted-foreground-1" dateTime={eintrag.erstellt} title={zeitVoll(eintrag.erstellt)}>
          {zeitText(eintrag.erstellt)}
        </time>
        <span className={eintrag.gelesen ? "block text-sm text-foreground break-words" : "block text-sm font-medium text-foreground break-words"}>{eintrag.titel}</span>
        {eintrag.text ? <span className="block text-sm text-muted-foreground-1 line-clamp-2 break-words">{eintrag.text}</span> : null}
      </span>
    </>
  );
  const klasse = eintrag.gelesen ? ZEILE : ZEILE_UNGELESEN;
  return ziel ? (
    <Link href={ziel} role="menuitem" className={klasse} onClick={() => beiZiel(eintrag)}>
      {inhalt}
    </Link>
  ) : markierenErlaubt ? (
    <button type="button" role="menuitem" className={klasse} onClick={() => beiMarkierenOhneZiel(eintrag)}>
      {inhalt}
    </button>
  ) : (
    // Unter Impersonation gibt es nichts zu markieren (r110): der Eintrag steht nur zum Lesen da, keine Schaltfläche, kein Ablehnungssatz.
    <div className={klasse}>{inhalt}</div>
  );
}
